import {
  Box,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DOMPurify from "dompurify";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import ChatInput from "./components/molecules/ChatInput/ChatInput";
import ConfigModal from "./components/organisms/ConfigModal/ConfigModal";
import ConfiguracionPanel from "./components/organisms/ConfiguracionPanel/ConfiguracionPanel";
import ConfirmDialog from "./components/organisms/ConfirmDialog/ConfirmDialog";
import EditDialog from "./components/organisms/EditDialog/EditDialog";
import LoadingScreen from "./components/organisms/LoadingScreen/LoadingScreen";
import Navbar from "./components/organisms/Navbar/Navbar";
import UsersModal from "./components/organisms/UsersModal/UsersModal";
import { API_URL } from "./config";
import { especialidadesServices } from "./services/EspecialidadesServices";
import { sistemaServices } from "./services/SistemaServices";
import CulturaLogo from "./shared/icons/culturait.svg";
import { Descripcion } from "./types/Descripcion";
import { EspecialidadesResponse } from "./types/EspecialidadesResponse";
import { Message } from "./types/Imessage";

function App() {
  const [isBackendReady, setIsBackendReady] = React.useState(false);
  const [showUsersTable, setShowUsersTable] = useState(false);
  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedSubespecialidades, setSelectedSubespecialidades] = useState<{
    [key: string]: string;
  }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Datos de usuarios hardcodeados
  const users = {
    "001/25": {
      nombre: "Nicolás Recalde",
      plan: "PLATINUM",
      dni: "28765432",
      direccion: "Av. Corrientes 1234, CABA",
      fecha_nacimiento: "15/03/1985",
    },
    "002/25": {
      nombre: "Federico Figueroa",
      plan: "ORO",
      dni: "30123456",
      direccion: "Av. Santa Fe 567, CABA",
      fecha_nacimiento: "22/07/1990",
    },
    "003/25": {
      nombre: "Agustin Figueroa",
      plan: "PLATA",
      dni: "25678901",
      direccion: "Av. Córdoba 890, CABA",
      fecha_nacimiento: "10/11/1988",
    },
    "004/25": {
      nombre: "Fulano Detal",
      plan: "BRONCE",
      dni: "33456789",
      direccion: "Av. Rivadavia 2345, CABA",
      fecha_nacimiento: "05/04/1995",
    },
    "005/25": {
      nombre: "Mengano Detal",
      plan: "CarBot+",
      dni: "35678901",
      direccion: "Av. Callao 678, CABA",
      fecha_nacimiento: "18/09/1992",
    },
  };

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [especialidades, setEspecialidades] =
    useState<EspecialidadesResponse | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const ws = useRef<WebSocket | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [valorModal, setValorModal] = useState("");
  const [editandoIndex, setEditandoIndex] = useState<number | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [modalType, setModalType] = useState<"descripcion" | "actualizar">(
    "descripcion"
  );
  const [navbarSelected, setNavbarSelected] = useState<
    "usuarios" | "config" | null
  >(null);
  // Estado para mostrar los botones de afiliado (por si luego cambia el flujo)
  const [showAffiliationButtons, setShowAffiliationButtons] = useState(true);
  // Estado para activar el chat completo
  const [chatActivo, setChatActivo] = useState(false);
  // Estado para controlar si el botón "Soy afiliado" está seleccionado
  const [isAffiliateSelected, setIsAffiliateSelected] = useState(false);
  // Estado para controlar el modal de usuarios
  const [showUsersModal, setShowUsersModal] = useState(false);

  useEffect(() => {
    const fetchEspecialidades = async () => {
      if (showConfig) {
        try {
          const data = await especialidadesServices.getEspecialidades();
          setEspecialidades(data);
        } catch (error) {
          console.error("Error al cargar especialidades:", error);
        }
      }
    };

    fetchEspecialidades();
  }, [showConfig]);

  useEffect(() => {
    if (!isBackendReady) return;

    // Conectar al WebSocket
    ws.current = new WebSocket(`${API_URL}/ws`);

    ws.current.onopen = () => {
      console.log("Conexión WebSocket establecida");
    };

    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setMessages((prev) => [...prev, { type: "bot", content: data.response }]);
      setIsLoading(false);
    };

    ws.current.onerror = (error) => {
      console.error("Error en WebSocket:", error);
    };

    ws.current.onclose = () => {
      setIsLoading(false);
      console.log("Conexión WebSocket cerrada");
    };

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [API_URL, isBackendReady]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (
      input.trim() &&
      ws.current &&
      ws.current.readyState === WebSocket.OPEN
    ) {
      const message = input.trim();
      if (messages.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            content: "¡Te damos la bienvenida a CarBot AI Agent!",
          },
        ]);
        setTimeout(() => {
          setMessages((prev) => [...prev, { type: "user", content: message }]);
          ws.current?.send(JSON.stringify({ message }));
          setInput("");
          setIsLoading(true);
        }, 500);
      } else {
        setMessages((prev) => [...prev, { type: "user", content: message }]);
        ws.current.send(JSON.stringify({ message }));
        setInput("");
        setIsLoading(true);
      }
    }
  };

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch(`${API_URL}/healthz`);
        if (response.ok) {
          setIsBackendReady(true);
        } else {
          setIsBackendReady(false);
          setTimeout(checkBackend, 5000);
        }
      } catch (error) {
        setIsBackendReady(false);
        setTimeout(checkBackend, 5000);
      }
    };

    checkBackend();
  }, [API_URL]);

  const handleOpenModal = (index?: number) => {
    setModalType("descripcion");
    setOpenModal(true);
    if (index !== undefined) {
      setEditandoIndex(index);
      // Encontrar la categoría que tiene la subespecialidad seleccionada
      const categoria = Object.keys(selectedSubespecialidades).find(
        (cat) => selectedSubespecialidades[cat] !== ""
      );

      if (categoria && especialidades) {
        const subespecialidad = especialidades[
          categoria
        ].subespecialidades.find(
          (sub) =>
            sub.uuid_especialidad === selectedSubespecialidades[categoria]
        );

        if (subespecialidad?.descripcion) {
          setValorModal(subespecialidad.descripcion[index]);
        }
      }
    } else {
      setEditandoIndex(null);
      setValorModal("");
    }
  };

  const handleOpenActualizarModal = () => {
    setModalType("actualizar");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setValorModal("");
  };

  const updateEspecialidadDescripcion = (
    uuid_especialidad: string,
    updateFn: (descripciones: string[]) => string[]
  ) => {
    setEspecialidades((prev) => {
      if (!prev) return prev;
      const newEspecialidades = { ...prev };
      const categoria = Object.keys(newEspecialidades).find((cat) =>
        newEspecialidades[cat].subespecialidades.some(
          (sub) => sub.uuid_especialidad === uuid_especialidad
        )
      );

      if (!categoria) return prev;

      newEspecialidades[categoria] = {
        ...newEspecialidades[categoria],
        esta_actualizado: 0,
      };

      const subIndex = newEspecialidades[categoria].subespecialidades.findIndex(
        (sub) => sub.uuid_especialidad === uuid_especialidad
      );

      if (subIndex !== -1) {
        const currentDescripciones = [
          ...newEspecialidades[categoria].subespecialidades[subIndex]
            .descripcion,
        ];
        newEspecialidades[categoria].subespecialidades[subIndex] = {
          ...newEspecialidades[categoria].subespecialidades[subIndex],
          descripcion: updateFn(currentDescripciones),
          esta_actualizado: 0,
        };
      }

      return newEspecialidades;
    });
  };

  const handleGuardarDescripcion = async (descripcion: Descripcion) => {
    try {
      if (editandoIndex !== null) {
        // Modo edición
        const categoria = Object.keys(especialidades || {}).find((cat) =>
          especialidades?.[cat].subespecialidades.some(
            (sub) => sub.uuid_especialidad === descripcion.uuid_especialidad
          )
        );

        if (!categoria || !especialidades) return;

        const subespecialidad = especialidades[
          categoria
        ].subespecialidades.find(
          (sub) => sub.uuid_especialidad === descripcion.uuid_especialidad
        );

        if (subespecialidad?.descripcion[editandoIndex]) {
          await especialidadesServices.editDescripcion(
            descripcion.uuid_especialidad,
            editandoIndex,
            descripcion.descripcion,
            0
          );

          // Actualizar el estado local
          updateEspecialidadDescripcion(
            descripcion.uuid_especialidad,
            (descripciones) => {
              const newDescripciones = [...descripciones];
              newDescripciones[editandoIndex] = descripcion.descripcion;
              return newDescripciones;
            }
          );
        }
      } else {
        // Modo creación
        await especialidadesServices.addDescripcion(descripcion);

        // Actualizar el estado local
        updateEspecialidadDescripcion(
          descripcion.uuid_especialidad,
          (descripciones) => [...descripciones, descripcion.descripcion]
        );
      }

      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar la descripción:", error);
    }
  };

  const handleOpenDeleteModal = (index: number) => {
    setDeleteIndex(index);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteIndex !== null && especialidades) {
      try {
        // Encontrar la categoría que tiene la subespecialidad seleccionada
        const categoria = Object.keys(selectedSubespecialidades).find(
          (cat) => selectedSubespecialidades[cat] !== ""
        );

        if (!categoria) return;

        const uuid_especialidad = selectedSubespecialidades[categoria];

        if (!uuid_especialidad) return;

        await especialidadesServices.deleteDescripcion(
          uuid_especialidad,
          deleteIndex,
          0
        );

        updateEspecialidadDescripcion(uuid_especialidad, (descripciones) => {
          const newDescripciones = [...descripciones];
          newDescripciones.splice(deleteIndex, 1);
          return newDescripciones;
        });

        handleCloseDeleteModal();
      } catch (error) {
        console.error("Error al eliminar la característica:", error);
      }
    }
  };

  function closeConfig() {
    setEspecialidades(null);
    setShowConfig(false);
    setExpandedItems({});
    setSelectedSubespecialidades({});
  }

  // Efecto para reiniciar los estados cuando se muestra la configuración
  useEffect(() => {
    if (showConfig) {
      setExpandedItems({});
      setSelectedSubespecialidades({});
    }
  }, [showConfig]);

  const handleActualizarSistema = async () => {
    try {
      setIsLoading(true);
      await sistemaServices.actualizarSistema(valorModal);
      setEspecialidades(null);
      setShowConfig(false);
      setExpandedItems({});
      setSelectedSubespecialidades({});
      setValorModal("");
      setIsLoading(false);
    } catch (error) {
      console.error("Error al actualizar el sistema:", error);
      setIsLoading(false);
    }
  };

  const renderMessageContent = (content: string) => {
    // Busca el primer div (inicio del HTML)
    const htmlStart = content.indexOf("<div");
    if (htmlStart !== -1) {
      const beforeHtml = content.slice(0, htmlStart).trim();
      const htmlContent = content.slice(htmlStart);

      return (
        <div>
          {beforeHtml && <ReactMarkdown>{beforeHtml}</ReactMarkdown>}
          <div
            style={{ marginTop: 8 }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(htmlContent),
            }}
          />
        </div>
      );
    }
    // Si no hay HTML, renderiza como Markdown
    return <ReactMarkdown>{content}</ReactMarkdown>;
  };

  const handleNavbarUsuarios = () => {
    setShowUsersModal(true);
    setShowConfig(false);
    setNavbarSelected("usuarios");
  };
  const handleNavbarConfig = () => {
    setShowConfig((prev) => !prev);
    setShowUsersTable(false);
    setNavbarSelected("config");
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setNavbarSelected(null);
  };

  useEffect(() => {
    if (showConfig) setNavbarSelected("config");
    else if (showUsersTable) setNavbarSelected("usuarios");
    else setNavbarSelected(null);
  }, [showConfig, showUsersTable]);

  // Removemos el return temprano para permitir que se muestre el overlay
  // en lugar de una pantalla completa de carga

  return (
    <Box
      sx={{
        background:
          "linear-gradient(90deg, #fff 0%, #f0f4ff 30%, #f0f4ff 70%, #fff 100%)",
      }}
    >
      <Box sx={{ height: "10vh", background: "transparent" }}>
        <Navbar
          onUsuarios={handleNavbarUsuarios}
          onConfig={handleNavbarConfig}
          selected={navbarSelected || undefined}
        />
      </Box>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "90vh",
          minWidth: "100vw",
        }}
      >
        {/* Overlay de carga cuando el backend no está listo */}
        {!isBackendReady && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: "rgba(89, 0, 255, 0.95)",
              backdropFilter: "blur(4px)",
            }}
          >
            <LoadingScreen message="Conectando con el servidor..." />
          </Box>
        )}
        <Box
          id="body"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(95vh - 48px - 60px)", // 100vh - navbar - footer
            width: "100%",
          }}
        >
          {/* Área de chat - 80% cuando hay mensajes, 0% cuando no hay */}
          <Box
            sx={{
              width: "100%",
              height: messages.length === 0 ? 0 : "80%",
              transition: "height 0.5s cubic-bezier(0.4,0,0.2,1)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: messages.length === 0 ? 0 : 2,
              pt: messages.length === 0 ? 0 : 3,
              pb: messages.length === 0 ? 0 : 4,
            }}
          >
            {messages.length > 0 && (
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "scroll",
                  overflowX: "hidden",
                  width: { xs: "95%", sm: "50%" },
                  height: "100%",
                  maxHeight: "100%",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#d5d5d5",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#bdbdbd",
                    },
                  },
                  // Forzar scrollbar siempre visible para evitar layout shift
                  scrollbarWidth: "thin",
                  scrollbarColor: "#d5d5d5 transparent",
                }}
              >
                <List>
                  {messages.map((message, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        justifyContent:
                          message.type === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1, sm: 2 },
                          maxWidth: { xs: "85%", sm: "70%" },
                          width: "fit-content",
                          display: "inline-flex",
                          bgcolor:
                            message.type === "user" ? "#fbfbfb" : "transparent",
                          color:
                            message.type === "user"
                              ? "#58575c"
                              : "text.primary",
                          border:
                            message.type === "user"
                              ? "1px solid #d5d5d5"
                              : "none",
                          borderRadius:
                            message.type === "user"
                              ? "24px 24px 8px 24px"
                              : "none",
                          "& .MuiListItemText-root": {
                            margin: 0,
                            width: "fit-content",
                          },
                          "& .calendario-turnos": {
                            width: "100%",
                            overflowX: "hidden",
                            "& .turnos-container": {
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "repeat(auto-fit, minmax(150px, 1fr))",
                                sm: "repeat(auto-fill, minmax(250px, 1fr))",
                              },
                              gap: { xs: "8px", sm: "20px" },
                              marginTop: { xs: "8px", sm: "20px" },
                              width: "100%",
                            },
                            "& .turno-card": {
                              backgroundColor: "white",
                              borderRadius: "8px",
                              padding: { xs: "8px", sm: "15px" },
                              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                              transition: "transform 0.2s",
                              width: { xs: "93%", sm: "100%", md: "100%" },
                              "&:hover": {
                                transform: "translateY(-2px)",
                              },
                            },
                            "& .turno-fecha": {
                              backgroundColor: "#1e3a8a !important",
                              color: "white !important",
                              padding: { xs: "6px", sm: "10px" },
                              borderRadius: "6px",
                              marginBottom: { xs: "6px", sm: "10px" },
                              width: "100%",
                            },
                            "& .dia": {
                              display: "block",
                              fontSize: { xs: "0.9em", sm: "1.2em" },
                              fontWeight: "bold",
                              wordBreak: "break-word",
                            },
                            "& .hora": {
                              display: "block",
                              fontSize: { xs: "0.85em", sm: "1.1em" },
                            },
                            "& .turno-info": {
                              padding: { xs: "6px", sm: "10px" },
                              width: "100%",
                              "& h3": {
                                color: "#2c3e50",
                                margin: 0,
                                fontSize: { xs: "0.9em", sm: "1.1em" },
                                wordBreak: "break-word",
                              },
                            },
                          },
                        }}
                      >
                        <ListItemText
                          sx={{
                            pl: { xs: message.type === "user" ? 1 : 0, sm: 0 },
                          }}
                          primary={
                            message.type === "user"
                              ? message.content
                              : renderMessageContent(message.content)
                          }
                        />
                      </Paper>
                    </ListItem>
                  ))}
                  {isLoading && (
                    <ListItem sx={{ justifyContent: "flex-start" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          maxWidth: "70%",
                          bgcolor: "transparent",
                          color: "text.primary",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                          escribiendo...
                        </Typography>
                      </Paper>
                    </ListItem>
                  )}
                  <div ref={messagesEndRef} />
                </List>
              </Box>
            )}
          </Box>

          {/* Área de input - 20% cuando hay mensajes, 100% cuando no hay */}
          <Box
            sx={{
              height: messages.length === 0 ? "100%" : "20%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: messages.length === 0 ? "center" : "center",
              width: "100%",
              transition: "height 0.5s cubic-bezier(0.4,0,0.2,1)",
              pb: messages.length === 0 ? 0 : 4,
            }}
          >
            <Box
              sx={{
                width: { xs: "95%", sm: "50%" },
                borderRadius: "18px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: messages.length === 0 ? "auto" : "120px",
              }}
            >
              {messages.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 500,
                      color: "#58575c",
                      fontSize: { xs: "1.3rem", md: "1.5rem" },
                    }}
                  >
                    ¡Te damos la bienvenida a CarBot!
                  </Typography>
                </Box>
              )}
              <ChatInput
                placeholder={
                  isAffiliateSelected
                    ? "Mi número de afiliado es..."
                    : messages.length === 0
                      ? "Escribe la consulta que tengas para CarBot..."
                      : "Escribe tu consulta..."
                }
                onSend={(text) => {
                  if (text.trim()) {
                    // Si el botón de afiliado está seleccionado, concatenar el texto
                    const messageContent = isAffiliateSelected
                      ? `Mi número de afiliado es ${text.trim()}`
                      : text.trim();

                    setShowAffiliationButtons(false);
                    setIsAffiliateSelected(false); // Resetear el estado del botón

                    // Si es el primer mensaje, agregar el mensaje de bienvenida ANTES del mensaje del usuario
                    if (messages.length === 0) {
                      setMessages((prev) => [
                        ...prev,
                        {
                          type: "bot",
                          content: "¡Te damos la bienvenida a CarBot AI Agent!",
                        },
                      ]);
                      setTimeout(() => {
                        if (
                          ws.current &&
                          ws.current.readyState === WebSocket.OPEN
                        ) {
                          setMessages((prev) => [
                            ...prev,
                            { type: "user", content: messageContent },
                          ]);
                          ws.current.send(
                            JSON.stringify({ message: messageContent })
                          );
                          setIsLoading(true);
                        }
                      }, 500);
                    } else {
                      // Usar la función handleSend original que envía al WebSocket
                      if (
                        ws.current &&
                        ws.current.readyState === WebSocket.OPEN
                      ) {
                        setMessages((prev) => [
                          ...prev,
                          { type: "user", content: messageContent },
                        ]);
                        ws.current.send(
                          JSON.stringify({ message: messageContent })
                        );
                        setIsLoading(true);
                      }
                    }
                  }
                }}
                disabled={isLoading}
              />
            </Box>
          </Box>
        </Box>

        <Box
          id="footer"
          sx={{
            width: "100%",
            py: 1,
            px: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "end" },
            gap: 2,
            flexWrap: "wrap",
            borderTop: 1,
            borderColor: "divider",
            background: "rgba(255, 255, 255, 0.8)",
            height: "60px",
            position: "fixed",
            bottom: 0,
            left: 0,
            zIndex: 1000,
            "@media (max-width: 600px)": { height: "auto", py: 2 },
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Powered by
          </Typography>
          <Box
            component="img"
            src={CulturaLogo}
            alt="Cultura Logo"
            sx={{ height: { xs: "20px", md: "30px" } }}
          />
        </Box>

        <EditDialog
          open={openModal}
          title={
            modalType === "actualizar"
              ? "Actualizar"
              : editandoIndex !== null
                ? "Editar Característica"
                : "Agregar Nueva Característica"
          }
          value={valorModal}
          onChange={setValorModal}
          onClose={handleCloseModal}
          onSave={() => {
            if (modalType === "actualizar") {
              handleActualizarSistema();
              handleCloseModal();
            } else {
              if (!especialidades) return;
              const categoria = Object.keys(selectedSubespecialidades).find(
                (cat) => selectedSubespecialidades[cat] !== ""
              );
              if (!categoria) return;
              const subespecialidad = especialidades[
                categoria
              ].subespecialidades.find(
                (sub) =>
                  sub.uuid_especialidad === selectedSubespecialidades[categoria]
              );
              if (!subespecialidad) return;
              handleGuardarDescripcion({
                uuid_especialidad: subespecialidad.uuid_especialidad,
                descripcion: valorModal,
              });
            }
          }}
          type={modalType === "actualizar" ? "password" : "text"}
          maxLength={modalType === "actualizar" ? undefined : 80}
          label={
            modalType === "actualizar"
              ? "Ingrese la key de actualización"
              : editandoIndex !== null
                ? "Editar Característica"
                : "Nueva Característica"
          }
        />

        <ConfirmDialog
          open={openDeleteModal}
          title="Confirmar Eliminación"
          message="¿Está seguro que desea eliminar esta descripción?"
          confirmText="Eliminar"
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteModal}
        />

        {/* Modal de Usuarios */}
        <UsersModal
          open={showUsersModal}
          onClose={handleCloseUsersModal}
          users={users}
        />
        <ConfigModal open={showConfig} onClose={closeConfig}>
          <ConfiguracionPanel
            especialidades={especialidades}
            isLoading={isLoading}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            selectedSubespecialidades={selectedSubespecialidades}
            setSelectedSubespecialidades={setSelectedSubespecialidades}
            isMobile={isMobile}
            handleOpenModal={handleOpenModal}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenActualizarModal={handleOpenActualizarModal}
            closeConfig={closeConfig}
          />
        </ConfigModal>
      </Container>
    </Box>
  );
}

export default App;
